import { useMemo, useState } from "react";
import { matchPlanWithDatasets, numericColumns } from "../../utils/match";
import { tl } from "../../utils/format";

export default function ResultsPanel({ plan, datasets }) {
  const planRows = useMemo(() => plan?.rows || [], [plan]);
  const { matches, totalMatches } = useMemo(
    () => matchPlanWithDatasets(planRows, datasets || []),
    [planRows, datasets]
  );

  // ortak metrik alanlarını çıkar (tüm eşleşenlerden birleşim)
  const allRows = matches.flatMap((m) => m.items);
  const numCols = useMemo(() => {
    const set = new Set();
    allRows.forEach((r) => numericColumns([r]).forEach((c) => set.add(c)));
    return Array.from(set);
  }, [allRows]);

  const [metric, setMetric] = useState(numCols[0] || null);

  const grand = useMemo(() => {
    if (!metric) return 0;
    return allRows.reduce((s, r) => s + (Number(r[metric]) || 0), 0);
  }, [allRows, metric]);

  if (!datasets || datasets.length === 0) return null;
  if (totalMatches === 0)
    return (
      <div className="card p-4">
        <div className="font-semibold mb-2">Plan Sonuçları</div>
        <div className="text-slate-600 text-sm">
          Yüklü veri setleri ile eşleşme bulunamadı.
        </div>
      </div>
    );

  return (
    <div className="card p-4 space-y-4">
      <div className="flex items-center gap-3">
        <div className="font-semibold">Plan Sonuçları</div>
        {numCols.length > 0 && (
          <div className="ms-auto flex items-center gap-2">
            <span className="text-sm text-slate-500">Metrik:</span>
            <select
              className="h-9 px-2 border border-slate-300 rounded-md bg-white"
              value={metric || ""}
              onChange={(e) => setMetric(e.target.value || null)}
            >
              {numCols.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Özet */}
      <div className="grid sm:grid-cols-3 gap-3 text-sm">
        <div className="card-sm p-3">
          <div className="text-slate-500">Eşleşen Satır</div>
          <div className="font-semibold">{matches.length} / {planRows.length}</div>
        </div>
        <div className="card-sm p-3">
          <div className="text-slate-500">Toplam Eşleşen Lokasyon</div>
          <div className="font-semibold">{totalMatches}</div>
        </div>
        <div className="card-sm p-3">
          <div className="text-slate-500">Seçilen Metrik Toplamı</div>
          <div className="font-semibold">
            {metric ? tl(grand) : "-"}
          </div>
        </div>
      </div>

      {/* Detay tablo */}
      {matches.map((m) => (
        <div key={m.planRowIndex} className="border border-slate-200 rounded-lg overflow-hidden">
          <div className="bg-slate-50/60 px-3 py-2 text-sm flex items-center justify-between">
            <div>
              <span className="font-medium">{m.plan.city}</span> ·{" "}
              <span className="text-slate-600">{m.plan.unit}</span>{" "}
              <span className="text-slate-400">
                ({m.plan.startDate} → {m.plan.endDate})
              </span>
            </div>
            <div className="text-slate-500">{m.items.length} lokasyon</div>
          </div>
          <div className="table-wrap overflow-auto">
            <table className="min-w-[900px] w-full text-sm">
              <thead>
                <tr className="text-slate-600">
                  <th className="cell font-semibold border-b border-slate-200 text-left">Dataset</th>
                  <th className="cell font-semibold border-b border-slate-200 text-left">Şehir</th>
                  <th className="cell font-semibold border-b border-slate-200 text-left">Tür</th>
                  <th className="cell font-semibold border-b border-slate-200 text-left">Adres</th>
                  <th className="cell font-semibold border-b border-slate-200 text-left">Buff</th>
                  {metric && (
                    <th className="cell font-semibold border-b border-slate-200 text-right">{metric}</th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white">
                {m.items.map((r, i) => (
                  <tr key={i} className={`border-b border-slate-100 ${i % 2 === 1 ? "bg-slate-50/40" : ""}`}>
                    <td className="cell">{r.__dataset}</td>
                    <td className="cell">{r.IL ?? r["İl"] ?? ""}</td>
                    <td className="cell">{r.TUR ?? r["Tür"] ?? ""}</td>
                    <td className="cell">{r.AD ?? r["Adres"] ?? r["ADRES"] ?? ""}</td>
                    <td className="cell">{r.BUFF_DIST ?? r["Buff"] ?? ""}</td>
                    {metric && (
                      <td className="cell-num">{Number(r[metric] || 0).toLocaleString("tr-TR")}</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}
